/*
 * Vendored replacement for react-habitat.
 *
 * Public API:
 *   register(name, value, options?) — register a component or async loader
 *   unRegisterAll()                  — clear the component registry
 *   disposeAll()                     — unmount all roots and stop observing
 *   setComponentSelector(sel)        — change the attribute used for mount discovery (default: data-component)
 *   populate()                       — scan the DOM and install the MutationObserver
 *
 * `register` accepts two kinds of values, distinguished by the `async` option:
 *   register('Counter', Counter)                                      // sync: React component
 *   register('Slider', () => import('./Slider'), { async: true })    // async: loader returning a module
 *
 * The caller (Bootstrapper.js) knows at registration time whether a value came
 * from a sync `habitat.js` or an async `habitat-async.js`, so it passes the
 * option explicitly. We intentionally do NOT sniff the value at mount time
 * (e.g. by calling it and checking for a thenable) because that would invoke
 * React components outside a render cycle — triggering hook errors for any
 * component that uses useState/useEffect.
 *
 * The module renders directly into the `[data-component]` element (no sibling
 * replacement). Each mounted element is tagged with `data-habitat="true"`
 * to prevent double-mount.
 */

// Side-effect import: ensures @wordpress/dependency-extraction-webpack-plugin
// registers `react-dom` in the .asset.php dependency list. Without this,
// WordPress won't enqueue React DOM before the theme script and createRoot fails.
// eslint-disable-next-line import/no-unassigned-import
import 'react-dom';
import React from 'react';
import { createRoot } from 'react-dom/client';

import { getElementProps } from './props';
import { observeMounts, observeTreeMutations } from './observer';

// Marker attribute set on the host element after mount. This name matches
// react-habitat's legacy attribute so downstream SCSS selectors like
// `[data-habitat] > .foo` keep working without migration. Note: react-habitat
// emitted `data-habitat="C<n>"` (container ID); the vendored module emits
// `data-habitat="true"` since we don't have container IDs.
const HABITAT_ATTR = 'data-habitat';

let componentSelector = 'data-component';
const components = new Map(); // name -> { value, isAsync }
const roots = new Map(); // Element -> Root
const observerSets = new Map(); // Document -> { mountObserver, unmountObserver }

/*
 * Generation counter for cancellation during HMR / disposeAll.
 *
 * Every call to `disposeAll()` increments this counter. `mountElement` captures
 * the current generation at entry; after each await it compares against the
 * live counter and aborts if they differ. This closes a time-of-check/time-of-use
 * hole where a mount in flight during disposeAll could complete against a stale
 * component registry and leave the element orphaned (marked `data-habitat`
 * but untracked in `roots`).
 */
let generation = 0;

const setComponentSelector = ( selector ) => {
	componentSelector = selector;
};

const register = ( name, value, options = {} ) => {
	components.set( name, { value, isAsync: !! options.async } );
};

const unRegisterAll = () => {
	components.clear();
};

const getComponentName = ( el ) => el.getAttribute( componentSelector );

const resolveComponent = async ( entry ) => {
	if ( ! entry.isAsync ) return entry.value;
	const mod = await entry.value();
	return ( mod && mod.default ) || mod;
};

const unmountRoot = ( el ) => {
	const root = roots.get( el );
	if ( ! root ) return;
	try {
		root.unmount();
	} catch ( e ) {
		// already unmounted
	}
	roots.delete( el );
};

const mountElement = async ( el ) => {
	const myGen = generation;

	if ( el.getAttribute( HABITAT_ATTR ) === 'true' ) return;

	const name = getComponentName( el );
	if ( ! name ) return;

	const entry = components.get( name );
	if ( ! entry ) return;

	// Mark synchronously to prevent re-entry from the MutationObserver before
	// the async resolution completes.
	el.setAttribute( HABITAT_ATTR, 'true' );

	let Component;
	try {
		Component = await resolveComponent( entry );
	} catch ( e ) {
		// eslint-disable-next-line no-console
		console.error( `habitat: failed to resolve component "${ name }"`, e );
		el.removeAttribute( HABITAT_ATTR );
		return;
	}

	// Aborted by disposeAll() while we were awaiting the loader — the element
	// and any stale references should be dropped without mounting.
	if ( myGen !== generation ) {
		el.removeAttribute( HABITAT_ATTR );
		return;
	}
	if ( ! Component ) {
		el.removeAttribute( HABITAT_ATTR );
		return;
	}

	const props = getElementProps( el );
	const root = createRoot( el );
	root.render( React.createElement( Component, props ) );
	roots.set( el, root );
};

/*
 * `root` is a Document — `document` by default, or an iframe's
 * `contentDocument` for editor-canvas / SandBox-style scenarios. Per-root
 * observers let one habitat instance watch multiple documents at once
 * (MutationObserver doesn't cross iframe boundaries on its own).
 */
const populate = ( root = document ) => {
	// Idempotent for this root: disconnect any previous observers before
	// installing new ones. Observers in other roots are left alone.
	const existing = observerSets.get( root );
	if ( existing ) {
		existing.mountObserver.disconnect();
		existing.unmountObserver.disconnect();
	}

	// Initial scan within this root
	const selector = `[${ componentSelector }]:not([${ HABITAT_ATTR }])`;
	const initial = root.querySelectorAll( selector );
	for ( let i = 0; i < initial.length; i++ ) {
		mountElement( initial[ i ] );
	}

	// Watch for dynamically added nodes within this root (Gutenberg editor,
	// SPA updates, etc.)
	const mountObserver = observeMounts( root, selector, mountElement );

	// Single shared unmount observer per root: on any DOM mutation, iterate
	// the tracked roots map and unmount any whose element is no longer
	// attached. `el.isConnected` is used instead of `document.contains` so
	// the check works regardless of which document the element belongs to.
	const unmountObserver = observeTreeMutations( root, () => {
		if ( roots.size === 0 ) return;
		for ( const el of Array.from( roots.keys() ) ) {
			if ( ! el.isConnected ) {
				unmountRoot( el );
			}
		}
	} );

	observerSets.set( root, { mountObserver, unmountObserver } );
};

const disposeAll = () => {
	// Bump the generation counter FIRST — any in-flight mountElement() awaits
	// will see the new value after their await resumes and abort cleanly.
	generation++;

	for ( const set of observerSets.values() ) {
		set.mountObserver.disconnect();
		set.unmountObserver.disconnect();
	}
	observerSets.clear();

	for ( const [ el, root ] of roots ) {
		try {
			root.unmount();
		} catch ( e ) {
			// ignore
		}
		if ( el && el.removeAttribute ) el.removeAttribute( HABITAT_ATTR );
	}
	roots.clear();
};

export { register, unRegisterAll, disposeAll, setComponentSelector, populate };
